import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ConfirmDialog,
  type ContextMenuItem,
  ListDetailPane,
  toast,
} from '@core/components';
import { useConfirmDialog } from '@core/hooks/useConfirmDialog';
import type { Design } from '../../api/design';
import {
  type DesignPreset,
  deleteDesignPreset,
  designPresetKeys,
  designPresetsQuery,
  reorderDesignPresets,
} from '../../api/designPreset';
import { useAppContext } from '../context/AppContext';
import {
  type PresetFeatures,
  getDesignPresetSummary,
  getDesignPresetSummaryContext,
} from '../utils/designPreset';
import DesignPresetDialog from './DesignPresetDialog';
import { SummarySegments } from './SummarySegments';

interface DesignPresetsPaneProps {
  design: Design;
  features: PresetFeatures;
  initialPresetId?: number;
}

export function DesignPresetsPane({
  design,
  features,
  initialPresetId,
}: DesignPresetsPaneProps) {
  const queryClient = useQueryClient();
  const {
    vestments,
    fabrics,
    colors,
    threads,
    trimmings,
    galoons,
    cords,
    tassels,
  } = useAppContext();
  const summaryCtx = getDesignPresetSummaryContext(design.vestmentType, {
    vestments,
    fabrics,
    colors,
    threads,
    trimmings,
    galoons,
    cords,
    tassels,
  });
  const {
    data: presets = [],
    isPending: loading,
    isError,
  } = useQuery(designPresetsQuery(design.id));
  const refresh = () =>
    queryClient.invalidateQueries({
      queryKey: designPresetKeys.byDesign(design.id),
    });

  const [selectedId, setSelectedId] = useState(initialPresetId);
  const selectedPreset =
    presets.find((preset) => preset.id === selectedId) ?? presets[0] ?? null;
  const selectPreset = (id: number) => setSelectedId(id);

  const [dialog, setDialog] = useState<{
    open: boolean;
    preset: DesignPreset | null;
  }>({ open: false, preset: null });
  const openAdd = () => setDialog({ open: true, preset: null });
  const openEdit = (preset: DesignPreset) => setDialog({ open: true, preset });

  const presetDelete = useConfirmDialog<DesignPreset>({
    onConfirm: (preset) => deleteDesignPreset(preset.id),
    onSuccess: refresh,
    successMessage: 'Preset deleted',
    errorMessage: 'Failed to delete preset',
  });

  const getPresetMenuItems = (preset: DesignPreset): ContextMenuItem[] => [
    { label: 'Edit', onClick: () => openEdit(preset) },
    {
      label: 'Delete',
      destructive: true,
      onClick: () => presetDelete.request(preset),
    },
  ];

  const onReorder = (next: DesignPreset[]) => {
    const queryKey = designPresetKeys.byDesign(design.id);
    queryClient.setQueryData(queryKey, next);
    reorderDesignPresets(
      design.id,
      next.map((preset) => preset.id)
    ).catch(() => {
      toast.error('Failed to reorder presets');
      void refresh();
    });
  };

  const addPreset = isError
    ? undefined
    : { label: 'Add Preset', onClick: openAdd, size: 'xs' as const };
  const emptyState = {
    description: isError ? 'Failed to load presets.' : 'No presets yet.',
  };

  const renderDetails = (preset: DesignPreset) => {
    const segments = getDesignPresetSummary(preset, features, summaryCtx);
    return (
      <>
        <Typography variant="subtitle2" noWrap sx={{ mb: 1 }}>
          {preset.name}
        </Typography>
        {segments.length ? (
          <SummarySegments segments={segments} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No values yet.
          </Typography>
        )}
      </>
    );
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: 200, md: 0 },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ListDetailPane<DesignPreset>
          items={presets}
          selected={selectedPreset}
          onSelect={selectPreset}
          selectLabel="Preset"
          getMenuItems={getPresetMenuItems}
          add={addPreset}
          onReorder={onReorder}
          loading={loading}
          emptyState={emptyState}
          sidebarWidth={200}
        >
          {selectedPreset ? renderDetails(selectedPreset) : null}
        </ListDetailPane>
      </Box>
      <DesignPresetDialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, preset: null })}
        design={design}
        features={features}
        preset={dialog.preset}
        onSuccess={(saved) => {
          selectPreset(saved.id);
          void refresh();
        }}
      />
      <ConfirmDialog
        open={presetDelete.open}
        title="Delete Preset"
        content={`Are you sure you want to delete "${presetDelete.item?.name}"?`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={() => void presetDelete.confirm()}
        onCancel={presetDelete.cancel}
        loading={presetDelete.loading}
      />
    </>
  );
}
